import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Creator {
  id: number;
  name: string;
  role: string;
  bio: string;
  followers: string;
  stories: string;
  rating: number;
  image: string;
  alt: string;
  verified: boolean;
}

interface CreatorSpotlightProps {
  creators: Creator[];
}

export default function CreatorSpotlight({ creators }: CreatorSpotlightProps) {
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="MicrophoneIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Featured Creators</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-4">
            Meet Our Storytelling Stars
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Talented voices bringing stories to life across India's diverse languages and cultures
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {creators.map((creator) => (
            <div
              key={creator.id}
              className="group bg-card rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-2"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <AppImage
                  src={creator.image}
                  alt={creator.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                
                {creator.verified && (
                  <div className="absolute top-4 right-4 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-warm">
                    <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-primary-foreground" />
                  </div>
                )}
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-1 group-hover:text-primary transition-colors">
                    {creator.name}
                  </h3>
                  <p className="text-sm text-primary font-semibold mb-2">{creator.role}</p>
                  <p className="text-sm text-text-secondary line-clamp-2">{creator.bio}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <p className="text-lg font-bold text-text-primary">{creator.followers}</p>
                    <p className="text-xs text-text-secondary">Followers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-text-primary">{creator.stories}</p>
                    <p className="text-xs text-text-secondary">Stories</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Icon name="StarIcon" size={14} variant="solid" className="text-accent" />
                      <p className="text-lg font-bold text-text-primary">{creator.rating}</p>
                    </div>
                    <p className="text-xs text-text-secondary">Rating</p>
                  </div>
                </div>

                <button className="w-full py-3 bg-primary/10 text-primary rounded-xl font-semibold hover:bg-primary hover:text-primary-foreground transition-all duration-300">
                  View Profile
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/creator-community"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-cta font-bold text-lg transition-all duration-300 hover:bg-primary-dark hover:shadow-warm-lg hover:scale-105"
          >
            <span>Join Creator Community</span>
            <Icon name="ArrowRightIcon" size={20} />
          </a>
        </div>
      </div>
    </section>
  );
}